import { useEffect, useEffectEvent, useState } from "react";
import type { ReplayReadiness } from "./replay-session-records";

export function useReplayLetterbox({
  ready,
  letterbox,
}: Readonly<{
  ready: ReplayReadiness | undefined;
  letterbox: "page" | undefined;
}>): string | null | undefined {
  const [background, setBackground] = useState<string | null>();
  const readMode = useEffectEvent(() => letterbox);
  useEffect(() => {
    if (!ready) return;

    // Capture the theme once so later theme changes do not navigate the running iframe.
    const root = document.documentElement;
    const pageStyle = getComputedStyle(root);
    const pageIsDark =
      pageStyle.colorScheme === "dark" ||
      root.classList.contains("dark") ||
      root.classList.contains("dark-warm");
    const color =
      pageIsDark && readMode() === "page"
        ? pageBackgroundHex(pageStyle.getPropertyValue("--bg"))
        : null;
    // oxlint-disable-next-line react/set-state-in-effect -- Snapshot computed page styles after readiness; render cannot read the DOM.
    setBackground(color);
  }, [ready]);
  return background;
}

const functionalRgb =
  /^rgba?\(\s*(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})(?:\s*,\s*|\s+)(\d{1,3})\s*(?:[,/]\s*[\d.]+\s*)?\)$/i;

function pageBackgroundHex(value: string): string | null {
  const trimmed = value.trim();

  if (/^#[0-9a-f]{6}$/i.test(trimmed)) return trimmed.slice(1).toLowerCase();

  if (/^#[0-9a-f]{3}$/i.test(trimmed))
    return trimmed
      .slice(1)
      .toLowerCase()
      .replace(/./g, (digit) => digit + digit);

  const rgb = functionalRgb.exec(trimmed);

  if (!rgb) return null;

  return rgb
    .slice(1, 4)
    .map((component) =>
      Math.min(255, Number(component)).toString(16).padStart(2, "0"),
    )
    .join("");
}
